import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Pagination,
  Stack,
  Typography,
} from "@mui/material";

import {
  formatTicketDate,
  TicketStatusChip,
  TicketTypeChip,
} from "components/support/TicketChips";
import { TICKET_PAGE_SIZE, useListTicketsQuery } from "features/support/tickets";

interface TicketListProps {
  page: number;
  onPageChange: (page: number) => void;
  onOpen: (id: number) => void;
  onNew: () => void;
  onClose: () => void;
}

const TicketList = ({
  page,
  onPageChange,
  onOpen,
  onNew,
  onClose,
}: TicketListProps) => {
  const { data, isLoading, isError } = useListTicketsQuery(page);
  // `data` is the last page that arrived, not only this page's, so the
  // paginator stays put while the next page loads.
  const pageCount = data ? Math.ceil(data.count / TICKET_PAGE_SIZE) : 1;

  const renderBody = () => {
    if (isLoading) {
      return (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress aria-label="Loading tickets" />
        </Box>
      );
    }
    if (isError || !data) {
      return <Alert severity="error">Failed to load tickets.</Alert>;
    }
    if (!data.results.length) {
      return (
        <Typography color="text.secondary" py={2}>
          No tickets yet. Use New Ticket to report an issue or suggest an
          improvement.
        </Typography>
      );
    }
    return (
      <List disablePadding>
        {data.results.map((ticket) => (
          <ListItemButton
            key={ticket.id}
            divider
            onClick={() => onOpen(ticket.id)}
            sx={{ alignItems: "flex-start", gap: 1.5 }}
          >
            <Stack spacing={0.5} pt={0.5} alignItems="flex-start">
              <TicketTypeChip type={ticket.type} />
              <TicketStatusChip status={ticket.status} />
            </Stack>
            <ListItemText
              primary={ticket.description}
              primaryTypographyProps={{ noWrap: true }}
              secondary={`${ticket.creator_email} · ${formatTicketDate(
                ticket.created_on,
              )}`}
              sx={{ minWidth: 0 }}
            />
          </ListItemButton>
        ))}
      </List>
    );
  };

  return (
    <>
      <DialogTitle>Support tickets</DialogTitle>
      <DialogContent dividers>
        {renderBody()}
        {pageCount > 1 && (
          <Box display="flex" justifyContent="center" pt={2}>
            <Pagination
              count={pageCount}
              page={page}
              onChange={(_event, value) => onPageChange(value)}
              size="small"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" onClick={onNew}>
          New Ticket
        </Button>
      </DialogActions>
    </>
  );
};

export default TicketList;
